import { BookOpen, CheckCircle2, Circle, Plus, RefreshCw, Save, Scissors } from 'lucide-react'
import { AudioSplitter } from '../../../../components/AudioSplitter'
import { Card } from '../../../../components/ui/Card'
import { StoryEditorModals } from './StoryEditorModals'
import { StoryReviewStep } from './StoryReviewStep'

const formatTime = (seconds) => {
  if (seconds === undefined || seconds === null || Number.isNaN(Number(seconds))) return '--'
  const total = Math.max(0, Number(seconds))
  const minutes = Math.floor(total / 60)
  const secs = Math.floor(total % 60)
  const deciseconds = Math.floor((total - Math.floor(total)) * 10)
  return `${minutes}:${secs.toString().padStart(2, '0')}.${deciseconds}`
}

export function StoryEditorView(props) {
  const {
    activeSeg, addSegment, canGoStep2, canGoStep3, createNewStory, currentId,
    demoStory, demoStoryId, doImportTemplate, embedded, ensureStepValid,
    fetchStories, handleDeleteStory, handleSave, handleSegmentTimeChange,
    handleSegmentsFromSplitter, importStoryId, importTitle, importingTemplate,
    loadStory, loadingList, loadingTemplates, message, moveSegment,
    nudgeSegmentEnd, nudgeSegmentStart, openTemplateModal, removeSegment,
    safeStoryId, saving, selectedSegmentIndex, selectedTemplateId,
    setImportStoryId, setImportTitle, setSelectedSegmentIndex,
    setSelectedTemplateId, setShowDeleteSegmentModal, setShowDeleteStoryModal,
    setShowNewStoryModal, setShowTemplateModal, setStep, setStory,
    showDeleteSegmentModal, showDeleteStoryModal, showNewStoryModal,
    showTemplateModal, startImportTemplate, step, stepMeta, stories, story,
    templates, tx, updateSegment, updateSegmentWindow,
  } = props
  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6'}>
      {!embedded && (
        <Card className="p-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-slate-50 border-2 border-slate-100 flex items-center justify-center">
              <BookOpen size={18} className="text-slate-600" />
            </div>
            <div>
              <h1 className="text-slate-900 text-xl font-extrabold leading-tight">{tx({ zh: '故事编辑器', en: 'Story Editor' })}</h1>
              <div className="text-slate-500 text-xs font-bold">
                {tx({ zh: 'Dev 工具页（不建议同步到 Prod）', en: 'Dev tool page (not recommended to sync to prod)' })}
              </div>
            </div>
          </div>

          {message ? (
            <div className="px-3 py-2 rounded-2xl bg-slate-50 border-2 border-slate-100 text-slate-600 text-sm font-bold">
              {message}
            </div>
          ) : null}
        </Card>
      )}

      <div className="grid md:grid-cols-[260px,1fr] gap-6">
        {/* 左侧故事列表 */}
        <Card className="p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-700 text-sm font-extrabold">{tx({ zh: '故事列表', en: 'Story list' })}</span>
            <div className="flex gap-2">
              <button
                onClick={fetchStories}
                className="p-2 rounded-2xl bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-800 transition-colors border-2 border-slate-100"
                title={tx({ zh: '刷新', en: 'Refresh' })}
              >
                <RefreshCw size={16} className={loadingList ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={openTemplateModal}
                className="px-3 py-2 rounded-2xl bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                title={tx({ zh: '从 story-template 导入范例', en: 'Import a sample from story-template' })}
              >
                {tx({ zh: '导入', en: 'Import' })}
              </button>
              <button
                onClick={() => setShowNewStoryModal(true)}
                className="p-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors border-2 border-emerald-600"
                title={tx({ zh: '新建', en: 'New' })}
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto space-y-1">
            {stories.map((s) => (
              <button
                key={s.id}
                onClick={() => loadStory(s.id)}
                className={`w-full text-left px-3 py-2 rounded-2xl text-sm border-2 transition-colors ${
                  currentId === s.id ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm' : 'bg-white border-slate-100 text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <div className="font-extrabold truncate">{s.title}</div>
                <div className={`text-xs truncate font-mono ${currentId === s.id ? 'text-white/80' : 'text-slate-500'}`}>{s.id}</div>
              </button>
            ))}
            {stories.length === 0 && (
              <div className="text-xs text-slate-500 font-medium">
                {tx({ zh: '暂无故事，点击右上角 + 创建', en: 'No stories yet. Click + to create.' })}
              </div>
            )}
          </div>
        </Card>

        {/* 右侧编辑区 */}
        <Card className="p-6 space-y-4">
          {!story ? (
            <div className="space-y-4">
              <div className="text-slate-900 text-lg font-extrabold">{tx({ zh: '从这里开始', en: 'Start here' })}</div>
              <div className="text-slate-500 text-sm font-medium">
                {tx({ zh: '先导入模板/示例，或新建一个故事。', en: 'Import a template/sample, or create a new story.' })}
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <button
                  onClick={openTemplateModal}
                  className="p-4 rounded-3xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100/60 transition-colors text-left"
                >
                  <div className="text-slate-900 font-extrabold">{tx({ zh: '导入范例', en: 'Import sample' })}</div>
                  <div className="text-slate-500 text-xs mt-1 font-medium">
                    {tx({ zh: '从 story-template 导入一份可编辑的故事', en: 'Import an editable story from story-template' })}
                  </div>
                </button>
                <button
                  onClick={() => (demoStory ? loadStory(demoStoryId) : setShowNewStoryModal(true))}
                  className="p-4 rounded-3xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100/60 transition-colors text-left"
                >
                  <div className="text-slate-900 font-extrabold">{tx({ zh: '打开示例', en: 'Open sample' })}</div>
                  <div className="text-slate-500 text-xs mt-1 font-medium">
                    {demoStory ? demoStory.title : tx({ zh: '没有示例时将新建一个故事', en: 'No sample found; will create a new story' })}
                  </div>
                </button>
                <button
                  onClick={() => setShowNewStoryModal(true)}
                  className="p-4 rounded-3xl border-2 border-slate-100 bg-white hover:bg-slate-50 transition-colors text-left"
                >
                  <div className="text-slate-900 font-extrabold">{tx({ zh: '新建故事', en: 'Create story' })}</div>
                  <div className="text-slate-500 text-xs mt-1 font-medium">
                    {tx({ zh: '创建一个 story_id（字母数字下划线）', en: 'Create a story_id (letters/numbers/underscore)' })}
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* 头部：标题/保存 */}
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="text-xs text-slate-500 font-bold">{tx({ zh: '故事 ID', en: 'Story ID' })}</div>
                  <div className="text-slate-800 font-mono text-sm font-bold">{story.id}</div>
                  <div className="mt-3">
                    <label className="block text-xs text-slate-500 mb-1 font-bold">{tx({ zh: '标题', en: 'Title' })}</label>
                    <input
                      type="text"
                      value={story.title}
                      onChange={(e) => setStory({ ...story, title: e.target.value })}
                      className="w-full bg-white border-2 border-slate-100 rounded-2xl px-3 py-2 text-slate-800 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-extrabold disabled:opacity-60 transition-colors border-2 border-emerald-600 shadow-sm"
                  >
                    <Save size={16} />
                    {tx({ zh: '保存', en: 'Save' })}
                  </button>
                  <button
                    onClick={() => setShowDeleteStoryModal(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-extrabold transition-colors border-2 border-rose-600"
                  >
                    <Trash2 size={14} />
                    {tx({ zh: '删除', en: 'Delete' })}
                  </button>
                </div>
              </div>

              {/* Stepper */}
              <div className="grid grid-cols-3 gap-2">
                {stepMeta.map((s) => {
                  const isActive = step === s.id
                  const disabled = (s.id === 2 && !canGoStep2) || (s.id === 3 && !canGoStep3)
                  const done = step > s.id
                  const Icon = done ? CheckCircle2 : Circle
                  return (
                    <button
                      key={s.id}
                      disabled={disabled}
                      onClick={() => setStep(ensureStepValid(s.id))}
                      className={`text-left p-3 rounded-3xl border-2 transition-colors ${
                        isActive ? 'border-emerald-200 bg-emerald-50' : 'border-slate-100 bg-white hover:bg-slate-50'
                      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} className={done ? 'text-emerald-600' : 'text-slate-300'} />
                        <div className="text-slate-800 text-sm font-extrabold">{s.title}</div>
                      </div>
                      <div className="text-slate-500 text-xs mt-1 font-medium">{s.desc}</div>
                      {s.id === 2 && !canGoStep2 && (
                        <div className="text-[11px] text-amber-600 mt-1 font-bold">
                          {tx({ zh: '先在第 1 步设置源音频', en: 'Set source audio in Step 1 first' })}
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>

              {/* Step 1 */}
              {step === 1 && (
                <div className="space-y-4">
	                  {!story.source_audio ? (
	                    <div className="p-4 rounded-3xl border-2 border-amber-200 bg-amber-50">
	                      <div className="text-slate-900 font-extrabold">{tx({ zh: '先把源音频放进来', en: 'Add the source audio first' })}</div>
	                      <div className="text-slate-600 text-xs mt-1 font-medium">
	                        {tx({
	                          zh: '拖拽音频到下面区域（或点上传）。完成后会自动亮起“下一步：切分”。',
	                          en: 'Drag audio into the area below (or click to upload). Then “Next: Split” will be enabled.',
	                        })}
	                      </div>
	                      <div className="mt-3">
	                        <AudioEditor
	                          value={story.source_audio}
	                          onChange={(url) => setStory((prev) => ({ ...prev, source_audio: url }))}
	                          label={tx({ zh: '源音频', en: 'Source audio' })}
	                        />
	                      </div>
	                    </div>
	                  ) : (
	                    <div className="p-4 rounded-3xl border-2 border-emerald-200 bg-emerald-50">
	                      <div className="flex items-center justify-between">
	                        <div>
	                          <div className="text-slate-900 font-extrabold">{tx({ zh: '源音频已就绪', en: 'Source audio ready' })}</div>
	                          <div className="text-slate-600 text-xs mt-1 font-medium">
	                            {tx({ zh: '直接进入切分，在波形上点几下生成段落。', en: 'Go to splitting and click the waveform to create segments.' })}
	                          </div>
	                        </div>
	                        <button
	                          onClick={() => setStory((prev) => ({ ...prev, source_audio: '' }))}
	                          className="px-3 py-2 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
	                        >
	                          {tx({ zh: '换音频', en: 'Change audio' })}
	                        </button>
	                      </div>
	                      <div className="mt-3">
	                        <AudioEditor
	                          value={story.source_audio}
	                          onChange={(url) => setStory((prev) => ({ ...prev, source_audio: url }))}
	                          label={tx({ zh: '源音频', en: 'Source audio' })}
	                        />
	                      </div>
	                    </div>
	                  )}
	                  <div className="flex items-center justify-between">
	                    <div className="text-xs text-slate-500 font-medium">
	                      {story.source_audio
	                        ? tx({ zh: '下一步：去切分（主按钮）', en: 'Next: go split (main button)' })
	                        : tx({ zh: '当前不可切分：先上传源音频', en: 'Cannot split yet: upload source audio first' })}
	                    </div>
	                    <button
	                      onClick={() => setStep(2)}
	                      disabled={!story.source_audio}
	                      className="px-4 py-2 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-extrabold disabled:opacity-50 transition-colors border-2 border-amber-500"
	                    >
	                      {tx({ zh: '下一步：切分', en: 'Next: Split' })}
	                    </button>
	                  </div>
	                </div>
	              )}

              {/* Step 2 */}
              {step === 2 && (
                <div className="space-y-4">
	                  {(story.segments || []).length > 0 && (
	                    <div className="p-4 rounded-3xl border-2 border-emerald-200 bg-emerald-50 flex items-center justify-between">
	                      <div>
	                        <div className="text-slate-900 font-extrabold">
	                          {tx({ zh: '已生成 {n} 个段落', en: '{n} segments generated' }, { n: story.segments.length })}
	                        </div>
	                        <div className="text-slate-500 text-xs mt-1 font-medium">
	                          {tx({ zh: '下一步去“校对”，把封面/文本补齐再保存。', en: 'Next: review, fill cover/text, then save.' })}
	                        </div>
	                      </div>
	                      <button
	                        onClick={() => setStep(3)}
	                        className="px-4 py-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-extrabold transition-colors border-2 border-emerald-600"
	                      >
	                        {tx({ zh: '下一步：校对', en: 'Next: Review' })}
	                      </button>
	                    </div>
	                  )}
                  <div className="flex items-center justify-between">
	                    <div className="text-slate-800 text-sm font-extrabold flex items-center gap-2">
	                      <Scissors size={16} className="text-slate-500" />
	                      {tx({ zh: '音频切分（点击波形添加切分点）', en: 'Audio split (click waveform to add points)' })}
	                    </div>
	                    <div className="flex items-center gap-2">
	                      <button onClick={() => setStep(1)} className="px-3 py-2 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold">
	                        {tx({ zh: '返回：选音频', en: 'Back: Audio' })}
	                      </button>
                      <button
                        onClick={() => setStep(3)}
                        disabled={!story.source_audio}
	                        className="px-3 py-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold disabled:opacity-50 transition-colors border-2 border-emerald-600"
	                      >
	                        {tx({ zh: '跳过：校对', en: 'Skip: Review' })}
	                      </button>
	                    </div>
	                  </div>

                  <AudioSplitter sourceAudio={story.source_audio} existingSegments={story.segments} onSegmentsGenerated={handleSegmentsFromSplitter} />
                </div>
              )}

              <StoryReviewStep
                tx={tx}
                story={story}
                setStep={setStep}
                handleSegmentTimeChange={handleSegmentTimeChange}
                addSegment={addSegment}
                selectedSegmentIndex={selectedSegmentIndex}
                setSelectedSegmentIndex={setSelectedSegmentIndex}
                activeSeg={activeSeg}
                moveSegment={moveSegment}
                setShowDeleteSegmentModal={setShowDeleteSegmentModal}
                updateSegment={updateSegment}
                nudgeSegmentStart={nudgeSegmentStart}
                nudgeSegmentEnd={nudgeSegmentEnd}
                updateSegmentWindow={updateSegmentWindow}
              />
            </>
          )}
        </Card>
      </div>

      <StoryEditorModals
        tx={tx}
        showNewStoryModal={showNewStoryModal}
        setShowNewStoryModal={setShowNewStoryModal}
        createNewStory={createNewStory}
        showDeleteStoryModal={showDeleteStoryModal}
        setShowDeleteStoryModal={setShowDeleteStoryModal}
        handleDeleteStory={handleDeleteStory}
        story={story}
        currentId={currentId}
        showDeleteSegmentModal={showDeleteSegmentModal}
        setShowDeleteSegmentModal={setShowDeleteSegmentModal}
        removeSegment={removeSegment}
        showTemplateModal={showTemplateModal}
        setShowTemplateModal={setShowTemplateModal}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        doImportTemplate={doImportTemplate}
        importingTemplate={importingTemplate}
        safeStoryId={safeStoryId}
        importStoryId={importStoryId}
        loadingTemplates={loadingTemplates}
        setImportStoryId={setImportStoryId}
        importTitle={importTitle}
        setImportTitle={setImportTitle}
        templates={templates}
        startImportTemplate={startImportTemplate}
      />
    </div>
  )
}
